// apps/widget/src/screens/routine.screen.tsx
//
// The clinic's own products, grouped AM/PM in the order they are applied.
//
// Slot order is not a display preference — serum after moisturiser does nothing,
// so the sequence is the advice.

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { send } from '../lib/postmessage';
import { useScan } from '../state';
import { ProductCard, type Product } from '../components/product-card';

interface Routine {
  am: Product[];
  pm: Product[];
  cartUrl?: string;
}

export function RoutineScreen() {
  const { state, go } = useScan();
  const [routine, setRoutine] = useState<Routine | null>(null);

  useEffect(() => {
    if (!state.scanId) return;
    void api
      .recommendations(state.scanId)
      .then((r) => setRoutine(r.routine as unknown as Routine))
      .catch(() => setRoutine({ am: [], pm: [] }));
  }, [state.scanId]);

  if (!routine) {
    return <div className="gs-skeleton" style={{ height: 320 }} aria-busy="true" />;
  }

  const empty = routine.am.length === 0 && routine.pm.length === 0;

  return (
    <div className="gs-stack">
      <h1 className="gs-title">{t('routine.title')}</h1>

      {/* No product matched: the consultation is where the routine gets made. */}
      {empty && <p className="gs-body">{t('routine.none', { clinic: state.config.tenant.name })}</p>}

      {routine.am.length > 0 && (
        <section>
          <h2 className="gs-section-title">{t('routine.morning')}</h2>
          {routine.am.map((product, i) => (
            <ProductCard key={product.id} product={product} step={i + 1} />
          ))}
        </section>
      )}

      {routine.pm.length > 0 && (
        <section>
          <h2 className="gs-section-title">{t('routine.evening')}</h2>
          {routine.pm.map((product, i) => (
            <ProductCard key={product.id} product={product} step={i + 1} />
          ))}
        </section>
      )}

      {/* One tap for the whole routine converts far better than five separate
          product links. Only offered when every product is in one shop. */}
      {routine.cartUrl && (
        <a
          className="gs-btn gs-btn--primary"
          href={routine.cartUrl}
          target="_blank"
          rel="noreferrer noopener"
          onClick={() => send('gs:cta:clicked', { mode: 'cart' })}
        >
          {t('routine.addAll')}
        </a>
      )}

      <button className="gs-btn gs-btn--ghost" onClick={() => go('booking')}>
        {t('recommendations.book')}
      </button>
    </div>
  );
}
